import axios from 'axios';
import { githubOrgRead, GithubReadError } from '@/apis/githubRead';
import { resolveGitlabBaseUrl } from '@/helpers/resolveGitlabBaseUrl';

/** Normalized row for imported org/group members (GitHub + GitLab). */
export interface GithubMember {
  login: string;
  avatar_url: string;
  name?: string;
  email?: string;
}

interface GithubMembersResult {
  members: GithubMember[];
  error: string;
}

/**
 * Members of the GitHub org, read through the backend.
 *
 * @param accessToken - Unused. The backend resolves the GitHub credential, so
 * the browser holds none; kept so existing call sites stay unchanged.
 */
export async function getGithubOrgMembers(
  orgName: string | undefined,
  accessToken: string
): Promise<GithubMembersResult> {
  if (!orgName) {
    return { members: [], error: 'Organization name is required' };
  }

  try {
    const members: GithubMember[] = [];
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const { data, has_more } = await githubOrgRead<GithubMember[]>({
        op: 'org_members',
        org: orgName,
        page,
        perPage: 100,
      });

      // The backend derives `has_more` from GitHub's Link header, so trust it
      // instead of inferring the end of the list from a short page.
      hasMorePages = has_more;
      page += 1;
      members.push(...(Array.isArray(data) ? data : []));
    }
    return { members, error: '' };
  } catch (error) {
    console.error('Error fetching GitHub organization members:', error);

    if (error instanceof GithubReadError) {
      // Callers branch on an error containing 401/403 to show the access-denied
      // path, so those codes stay verbatim. `integration_missing` (the PR bot is
      // not installed for this org) is the same dead end for the caller, so it
      // reports as 403 rather than a generic failure.
      if (error.status === 401) return { members: [], error: '401' };
      if (error.status === 403 || error.code === 'integration_missing') {
        return { members: [], error: '403' };
      }
      if (error.status === 404) {
        return {
          members: [],
          error: `GitHub organization "${orgName}" not found`,
        };
      }
    }

    return {
      members: [],
      error:
        'We could not load members from GitHub. Please try again in a moment.',
    };
  }
}

interface GitlabMembersResult {
  members: GithubMember[];
  error: string;
}

type GitlabGroupMemberRow = {
  username?: string;
  name?: string | null;
  avatar_url?: string;
  email?: string | null;
  public_email?: string | null;
  user?: {
    email?: string | null;
    public_email?: string | null;
  };
};

function emailFromGitlabGroupMemberRow(
  row: GitlabGroupMemberRow
): string | undefined {
  const pick = (v: string | null | undefined) =>
    typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;
  return (
    pick(row.email) ??
    pick(row.public_email) ??
    pick(row.user?.email) ??
    pick(row.user?.public_email)
  );
}

/** Best-effort public email for a GitLab username (public_email, or email when API exposes it). */
export async function fetchGitlabUserPublicEmail(
  username: string,
  accessToken: string,
  authUrl?: string | null
): Promise<string> {
  const u = username.trim();
  if (!u) return '';
  try {
    const base = resolveGitlabBaseUrl(authUrl);
    const response = await axios.get<
      Array<{ email?: string | null; public_email?: string | null }>
    >(`${base}/api/v4/users`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: { username: u },
    });
    const row = response.data[0];
    if (!row) return '';
    const publicEmail =
      typeof row.public_email === 'string' && row.public_email.trim().length > 0
        ? row.public_email.trim()
        : '';
    const privateEmail =
      typeof row.email === 'string' && row.email.trim().length > 0
        ? row.email.trim()
        : '';
    return publicEmail || privateEmail;
  } catch {
    console.warn(
      `[importMembers] Could not fetch GitLab public email for username "${username.trim()}"`
    );
    return '';
  }
}

type GitlabGroupListRow = {
  full_path?: string;
};

/**
 * Full paths of GitLab groups the authenticated user belongs to (for multi-group onboarding).
 * Uses GET /groups?membership=true — same host as the OAuth token (gitlab.com or self‑hosted).
 */
export async function listGitlabMembershipGroupFullPaths(
  accessToken: string,
  authUrl?: string
): Promise<{ paths: string[]; error: string }> {
  if (!accessToken.trim()) {
    return { paths: [], error: 'Access token is required' };
  }

  try {
    const base = resolveGitlabBaseUrl(authUrl);
    const paths: string[] = [];
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await axios.get<GitlabGroupListRow[]>(
        `${base}/api/v4/groups`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            membership: true,
            per_page: 100,
            page,
          },
        }
      );

      for (const row of response.data) {
        const fp =
          typeof row.full_path === 'string' ? row.full_path.trim() : '';
        if (fp) {
          paths.push(fp);
        }
      }

      if (response.data.length < 100) {
        hasMorePages = false;
      } else {
        page += 1;
      }
    }

    return { paths, error: '' };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        return {
          paths: [],
          error: error.response.status === 401 ? '401' : '403',
        };
      }
      return {
        paths: [],
        error: `GitLab API error: ${error.response?.data?.message || error.message}`,
      };
    }

    return {
      paths: [],
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export async function getGitlabGroupMembers(
  groupId: string | undefined,
  accessToken: string,
  authUrl?: string
): Promise<GitlabMembersResult> {
  if (!groupId) {
    return { members: [], error: 'Group ID is required' };
  }

  try {
    const base = resolveGitlabBaseUrl(authUrl);
    const members: GithubMember[] = [];
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await axios.get(
        `${base}/api/v4/groups/${encodeURIComponent(groupId as string)}/members/all`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            per_page: 100,
            page,
          },
        }
      );

      if (response.data.length < 100) {
        hasMorePages = false;
      } else {
        page++;
      }
      members.push(
        ...response.data.map((member: GitlabGroupMemberRow) => {
          const email = emailFromGitlabGroupMemberRow(member);
          return {
            login: member.username ?? '',
            avatar_url: member.avatar_url ?? '',
            name: member.name ?? '',
            ...(email ? { email } : {}),
          };
        })
      );
    }
    return { members, error: '' };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        return {
          members: [],
          error: error.response.status === 401 ? '401' : '403',
        };
      } else if (error.response?.status === 404) {
        return {
          members: [],
          error: `GitLab group with ID "${groupId}" not found`,
        };
      } else {
        return {
          members: [],
          error: `GitLab API error: ${error.response?.data?.message || error.message}`,
        };
      }
    }

    return {
      members: [],
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

interface BitbucketMember {
  login: string;
  name: string;
  avatar_url: string;
  id: string;
}

interface BitbucketMemberEntry {
  user?: {
    nickname?: string;
    display_name?: string;
    account_id?: string;
    links?: { avatar?: { href?: string } };
  };
}

interface BitbucketMembersPageResponse {
  values?: BitbucketMemberEntry[];
  next?: string;
}

export const getBitbucketWorkspaceMembers = async (
  workspaceName: string,
  accessToken: string
): Promise<BitbucketMember[]> => {
  if (!workspaceName?.trim()) {
    throw new Error('workspaceName is required');
  }
  if (!accessToken) {
    throw new Error('accessToken is required');
  }
  const members: BitbucketMemberEntry[] = [];
  // Route through the Next.js proxy to avoid CORS issues with direct Bitbucket API calls.
  let url: string | null =
    `/api/bitbucket/proxy/workspaces/${encodeURIComponent(workspaceName.trim())}/members?pagelen=100`;
  let fetchError: Error | null = null;

  while (url) {
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        throw new Error(`Bitbucket members API error: ${response.status}`);
      }
      const data: BitbucketMembersPageResponse = await response.json();
      members.push(...(data.values ?? []));
      // For paginated results via the proxy, the 'next' URL from Bitbucket
      // points to api.bitbucket.org directly. We need to re-route it through
      // the proxy. Extract the path after /2.0/ and prepend the proxy prefix.
      const nextUrl: string | undefined = data.next;
      if (nextUrl) {
        // Parse the absolute Bitbucket URL so we can separate path from query string.
        // Passing the full captured string (path + query) as the proxy path would cause
        // the proxy's searchParams loop to append the query params a second time.
        try {
          const parsed = new URL(nextUrl);
          const match = parsed.pathname.match(/\/2\.0\/(.+)/);
          if (match) {
            // Reconstruct as proxy path + query string (no double-appending)
            url = `/api/bitbucket/proxy/${match[1]}${parsed.search}`;
          } else {
            console.warn(
              'getBitbucketWorkspaceMembers: unexpected next URL format, pagination truncated:',
              nextUrl
            );
            url = null;
          }
        } catch {
          console.warn(
            'getBitbucketWorkspaceMembers: could not parse next URL, pagination truncated:',
            nextUrl
          );
          url = null;
        }
      } else {
        url = null;
      }
    } catch (error) {
      fetchError = error as Error;
      break;
    }
  }

  if (fetchError) {
    throw fetchError;
  }

  // Normalize to match the format expected by the onboarding flow.
  // Bitbucket's workspace members API does not return email addresses — the email
  // field is intentionally omitted here; callers should not rely on it being present.
  return members
    .filter((m: BitbucketMemberEntry) => !!m.user?.account_id)
    .map((m: BitbucketMemberEntry) => ({
      login: m.user?.nickname ?? '',
      name: m.user?.display_name ?? '',
      avatar_url: m.user?.links?.avatar?.href ?? '',
      id: m.user!.account_id!,
    }));
};
